import { classNames } from "@greeter/util";
import { useEffect, useRef, useState } from "react";
import {
  DateOnly,
  DateOnlySchema,
  TimeOfDay,
  TimeOfDayPeriod,
  toDateOnlyString,
} from "@greeter/date";

import { set } from "date-fns";
import { Log } from "@greeter/log";

import css from "./DateTimePeriodInput.module.scss";

function canParse(s: string): boolean {
  const d = new Date(s);
  return !isNaN(Number(d));
}

export type DateTimePeriod = {
  date: DateOnly;
  timePeriod: TimeOfDayPeriod;
};

export type DateTimePeriodInputProps = {
  value: DateTimePeriod;
  onValueChange?: (period: DateTimePeriod) => void;

  error?: React.ReactNode;
  warning?: React.ReactNode;
} & React.ComponentProps<"div">;

export function DateTimePeriodInput({
  className,
  ...props
}: DateTimePeriodInputProps) {
  const dateFromInputRef = useRef<HTMLInputElement | null>(null);
  const timeFromInputRef = useRef<HTMLInputElement | null>(null);
  const timeToInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const value = props.value ?? new Date();

    if (!dateFromInputRef.current) return;
    dateFromInputRef.current.value = value.date;
    if (!timeFromInputRef.current) return;
    timeFromInputRef.current.value = value.timePeriod.from.toString();
    if (!timeToInputRef.current) return;
    timeToInputRef.current.value = value.timePeriod.to.toString();
  }, [props.value]);

  return (
    <div
      {...classNames(css.DateTimePeriodInputContainer, className, css.Light)}
    >
      <div
        {...classNames(
          css.DateTimePeriodInput,
          props.error
            ? "error-outline"
            : props.warning
            ? "warning-outline"
            : undefined
        )}
      >
        <div className={css.DateInputContainer}>
          <label className={css.InputLabel}>Dato</label>
          <input
            ref={dateFromInputRef}
            {...classNames(css.Input, css.DateInput)}
            type="date"
            onBlur={(ev) => {
              const parseable = canParse(ev.currentTarget.value);
              if (!parseable) {
                Log.debug(
                  "[DateTimePeriodInput] could not parse the Date value",
                  {
                    input: ev.currentTarget.value,
                  }
                );
                return;
              }
              const d = new Date(ev.currentTarget.value);
              Log.debug("[DateTimePeriodInput] parsed as date", { d });
              const n = {
                ...props.value,
                date: DateOnlySchema.parse(
                  set(props.value.date, {
                    year: d.getFullYear(),
                    month: d.getMonth(),
                    date: d.getDate(),
                  })
                ),
              } satisfies DateTimePeriod;
              props.onValueChange?.(n);
              props.onBlur?.(ev);
            }}
          />
        </div>
        <div className={css.InputContainer}>
          <label className={css.InputLabel}>Fra kl.</label>
          <input
            ref={timeFromInputRef}
            {...classNames(css.Input, css.TimeInput)}
            type="time"
            onBlur={(ev) => {
              try {
                const timeOfDay = TimeOfDay.parse(ev.currentTarget.value);
                const n = {
                  ...props.value,
                  timePeriod: {
                    ...props.value.timePeriod,
                    from: timeOfDay,
                  },
                } satisfies DateTimePeriod;
                props.onValueChange?.(n);
              } catch (error) {
                Log.debug(
                  "[DateTimePeriodInput] could not parse value as a TimeOfDay",
                  { input: ev.currentTarget.value }
                );
              }
              props.onBlur?.(ev);
            }}
          />
        </div>
        <div className={css.InputContainer}>
          <label className={css.InputLabel}>Til kl.</label>
          <input
            ref={timeToInputRef}
            {...classNames(css.Input, css.TimeInput)}
            type="time"
            onBlur={(ev) => {
              try {
                const timeOfDay = TimeOfDay.parse(ev.currentTarget.value);
                const n = {
                  ...props.value,
                  timePeriod: {
                    ...props.value.timePeriod,
                    to: timeOfDay,
                  },
                } satisfies DateTimePeriod;
                props.onValueChange?.(n);
              } catch (error) {
                Log.debug(
                  "[DateTimePeriodInput] could not parse value as a TimeOfDay",
                  { input: ev.currentTarget.value }
                );
              }
              props.onBlur?.(ev);
            }}
          />
        </div>
      </div>
      {props.error}
      {!props.error && props.warning}
    </div>
  );
}
